import { Code, ConnectError } from "@connectrpc/connect";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { QueryStateFallback } from "@/components/QueryStateFallback";
import { useGetReservation } from "@/features/reservation/api/useGetReservation";
import { RefundInfoSection } from "@/features/reservation/components/RefundInfoSection";
import { ReservationSummaryCard } from "@/features/reservation/components/ReservationSummaryCard";
import { colors } from "@/theme/colors";

/**
 * 予約キャンセル完了画面 (/reservations/{予約トークン}/cancel/complete)。CancelReservation 成功後に
 * 遷移し、GetReservation で取得した予約内容 (店舗名・日時・購入内容) と返金内容 (支払済金額・
 * キャンセル料・返金額) を完了メッセージとともに表示する。
 *
 * 返金額はキャンセル確定時に記録された値 (GetReservation の cancel_fee / refund_amount。§7.4)。
 * 表示・認可はすべて予約アクセストークン (ReservationTokenLayout が interceptor へ渡す) に基づく。
 */
export function CancelCompleteStep() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useGetReservation();

  const reservation = data?.reservation;
  const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;

  if (!reservation) {
    return (
      <QueryStateFallback
        isLoading={isLoading}
        // 取得は成功したが reservation が空のケースも「見つからない」として扱う。
        // 以前は isLoading 側で吸収しておりスピナーが回り続けていた (ShopDetailView と同じ考え方)。
        isNotFound={isNotFound || (!isLoading && !isError)}
        isError={isError}
        notFound={{
          title: t("cancelComplete.notFound"),
          description: t("cancelComplete.notFoundDescription"),
        }}
        error={{
          title: t("cancelComplete.error"),
          description: t("cancelComplete.errorDescription"),
          actions: [{ label: t("errorPage.reload"), onClick: () => void refetch() }],
        }}
      />
    );
  }

  const paidAmount = Number(reservation.totalAmount);
  const cancelFee = Number(reservation.cancelFee ?? 0n);
  const refundAmount = Number(reservation.refundAmount ?? 0n);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 640, width: "100%", mx: "auto" }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: colors.text, textAlign: "center", pt: 2, pb: 5 }}
      >
        {t("cancelComplete.title")}
      </Typography>

      <ReservationSummaryCard
        reservation={reservation}
        message={t("cancelComplete.message")}
        showConfirmIcon
      />

      <Divider sx={{ my: 3 }} />

      <RefundInfoSection
        paidAmount={paidAmount}
        cancelFee={cancelFee}
        refundAmount={refundAmount}
        cardLast4={reservation.cardLast4}
      />
    </Box>
  );
}
